import jwtDecode from 'jwt-decode';
import { AnyAction, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';

import {
  ExportExtractMethod,
  StatementFileType,
  TransactionFlow,
} from '@portal/models/enumerators';

import AccountRequests from '~/repositories/account';
import * as MessageService from '~/services/message';

import { getStackPath } from '@portal/config/routes';
import { EventType } from '@portal/enum/analytics';
import ErrorsMessages from '@portal/enum/errors';
import { setHeader } from '@portal/repositories/securitySdkConfig';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import StorageService from '@portal/services/storage';
import NavigationService from '~/services/navigation';
import { AuthActions, LoadingActions, UserActions } from '.';
import {
  CLEAN_BALANCE,
  INVESTMENT_POSITION_REPORT,
  SET_OVERDRAFT_INFO,
  TRANSFER_TRANSACTIONS,
  USER_ACCOUNT_SPLIT_BALANCE,
  USER_BALANCE,
  USER_BALANCE_REQUEST_ERROR,
  USER_CONFIG,
  USER_EXPORT_DOWNLOAD_EXTRACT,
  USER_INCOME_TAX,
  USER_SIDE_BALANCE,
} from './actionTypes';
import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

export type GetUserBalanceAction = {
  payload: models.Balance;
  type: string;
};

export const getUserBalance =
  (paymentAccountId?: string, isEscrow?: boolean, isAccountSplit?: boolean) =>
  async (dispatch: Dispatch<LoadingAction | GetUserBalanceAction>) => {
    dispatch(LoadingActions.increaseBalanceLoading());

    try {
      const payload = await AccountRequests.getUserBalance(paymentAccountId);

      if (isEscrow) {
        dispatch({
          payload,
          type: USER_SIDE_BALANCE,
        });

        return;
      }

      if (isAccountSplit) {
        dispatch({
          payload,
          type: USER_ACCOUNT_SPLIT_BALANCE,
        });

        return;
      }

      dispatch({
        payload,
        type: USER_BALANCE,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch({
          type: USER_BALANCE_REQUEST_ERROR,
        });
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(LoadingActions.decreaseBalanceLoading());
    }
  };

export type GetExtractAction = {
  payload: { rows: bank.StatementItem[] };
  type: string;
};

export const getExtract =
  (flow?: TransactionFlow, params?: models.StatementSearchParams) =>
  async (dispatch: Dispatch<LoadingAction | GetExtractAction>) => {
    dispatch(LoadingActions.increaseExtractLoading());
    try {
      const payload = await AccountRequests.getExtract(flow, params);

      dispatch({
        payload,
        type: TRANSFER_TRANSACTIONS,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(LoadingActions.decreaseExtractLoading());
    }
  };

export type GetIncomeReport = {
  type: string;
  payload: string;
};

export const getIncomeReport =
  (year: string, callback: (err: Error | null) => void) =>
  async (dispatch: Dispatch<GetIncomeReport | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload = await AccountRequests.getIncomeReport(year);

      dispatch({
        payload,
        type: USER_INCOME_TAX,
      });
      callback(null);
    } catch (err) {
      if (err instanceof Error) {
        callback(err);
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export type GetExportExtractAction = {
  type: string;
  payload: {
    pdf: string;
    csv: string;
    ofx: string;
  } | null;
};

export const getExportExtract =
  (
    method: ExportExtractMethod,
    startAt: string,
    endAt: string,
    order: 'asc' | 'desc',
    fileType: StatementFileType
  ) =>
  async (dispatch: Dispatch<GetExportExtractAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      if (method === ExportExtractMethod.EMAIL) {
        await AccountRequests.getExportExtractByEmail(
          startAt,
          endAt,
          order,
          fileType
        );

        MessageService.success('PAGES.EXTRACT.EXPORT.EMAIL');
      } else {
        const payload = await AccountRequests.getExportExtractToDownload(
          startAt,
          endAt,
          order,
          fileType
        );

        dispatch({
          payload: {
            pdf: payload.pdf,
            csv: payload.csv,
            ofx: payload.ofx,
          },
          type: USER_EXPORT_DOWNLOAD_EXTRACT,
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export const cleanExportDownloadUrl =
  () => (dispatch: Dispatch<GetExportExtractAction>) =>
    dispatch({
      payload: null,
      type: USER_EXPORT_DOWNLOAD_EXTRACT,
    });

export const cleanUserBalance =
  () => async (dispatch: Dispatch<LoadingAction>) => {
    dispatch(increaseLoading());
    dispatch({
      type: CLEAN_BALANCE,
    });
    dispatch(LoadingActions.decreaseLoading());
  };

export const requestClosure =
  (comment?: string) => async (dispatch: Dispatch<LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      await AccountRequests.requestClosure(comment);
      MessageService.success('PAGES.AUTH.LOGIN.MESSAGES.CLOSE_ACCOUNT');
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export const updateProfilePhoto =
  (
    image: Buffer
  ): ThunkAction<void, reducers.rootReducer, unknown, LoadingAction> =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const { url } = await AccountRequests.getProfilePhotoUploadUrl();
      await AccountRequests.putProfilePhoto(image, url);
      sendEvent(EventType.CHANGE_PROFILE_PHOTO);
      dispatch(UserActions.getMe());
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export interface UserConfigAction {
  type: string;
  payload: models.Config[];
}

export const getUserConfig =
  (paymentAccountId?: string) =>
  async (dispatch: Dispatch<UserConfigAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload = await AccountRequests.getUserConfig(paymentAccountId);
      dispatch({
        type: USER_CONFIG,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const putUserConfig =
  (
    id: string,
    config: models.Config
  ): ThunkAction<void, reducers.rootReducer, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await AccountRequests.putUserConfig(id, config);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
        dispatch(getUserConfig());
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };

export type InvestmentPosition = {
  type: string;
  payload: string;
};

export const getInvestmentPosition =
  (searchParams?: advancedFilterModels.InvestmentPositionAdvancedFilter) =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const payload = await AccountRequests.getInvestmentPosition(searchParams);

      dispatch({
        type: INVESTMENT_POSITION_REPORT,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getOverdraftInfo = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const response = await AccountRequests.getOverdraftInfo();
    dispatch({
      type: SET_OVERDRAFT_INFO,
      payload: {
        ...response,
      },
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(
        translate('COMPONENTS.SIDEBAR_PROFILE.OVERDRAFT_INFO.LOAD_ERROR'),
        { originalError: err }
      );
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const switchAccount =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (id: string) => async (dispatch: Dispatch<any>) => {
    dispatch(increaseLoading());
    try {
      const data = await AccountRequests.switchAccount(id);

      setHeader(
        StorageService.StorageItems.AUTHORIZATION,
        `Bearer ${data.token}`
      );

      const decodedToken: models.DecodedToken = jwtDecode(data.token);

      const sessionToken = {
        accessToken: data.token,
        refreshToken: data.refreshToken,
      };

      StorageService.setItem(
        StorageService.StorageItems.SESSION_TOKEN,
        JSON.stringify(sessionToken)
      );

      if (decodedToken.impersonator) {
        const originalUserId = decodedToken.impersonator.id;
        const publicKey = StorageService.getItem(`publicKey_${originalUserId}`);
        const privateKey = StorageService.getItem(
          `privateKey_${originalUserId}`
        );
        const symmetricKey = StorageService.getItem(
          `symmetricKey_${originalUserId}`
        );
        const iv = StorageService.getItem(`iv_${originalUserId}`);

        if (publicKey && privateKey) {
          StorageService.setItem(`publicKey_${decodedToken.userId}`, publicKey);
          StorageService.setItem(
            `privateKey_${decodedToken.userId}`,
            privateKey
          );
        }

        if (symmetricKey) {
          StorageService.setItem(
            `symmetricKey_${decodedToken.userId}`,
            symmetricKey
          );
        }
        if (iv) {
          StorageService.setItem(`iv_${decodedToken.userId}`, iv);
        }
      }

      setTimeout(() => {
        window.location.href = getStackPath('HOME');
      }, 400);
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === ErrorsMessages.MAX_DEVICES_PER_USER
      ) {
        MessageService.error('MAX_DEVICES_REACHED');
        dispatch(increaseLoading());
        await new Promise((resolve) => setTimeout(resolve, 2000));
        dispatch(decreaseLoading());

        dispatch(AuthActions.logout());
        NavigationService.navigate('/');
        return;
      }

      dispatch(AuthActions.logout());

      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
        throw err;
      }
    } finally {
      dispatch(LoadingActions.decreaseLoading());
    }
  };
